import { Source, IReadOptions, IPaginatedList } from '@samc/common';

/**
 * Same behavior as {@link CachedDataSource} for workflow QB folder trees, but loads {@link loadData}
 * only once per cache fill (avoids duplicate HTTP when using the shared CachedDataSource implementation).
 */
export abstract class WorkflowQbTreeCachedSource<T> extends Source<T> {
  private _items: T[] | null = null;

  public get isLoaded(): boolean {
    return this._items !== null;
  }

  public get cachedItems(): T[] {
    return this._items ?? [];
  }

  public constructor(pageSize = Number.MAX_SAFE_INTEGER) {
    super(pageSize);
  }

  protected abstract loadData(options: IReadOptions, abortController: AbortController): Promise<T[]> | T[];

  protected getStaticData(): T[] {
    return [];
  }

  protected async getRows(options: IReadOptions, abortController: AbortController): Promise<IPaginatedList<T>> {
    if (this._items === null) {
      try {
        let data = this.loadData(options, abortController);
        if (!Array.isArray(data)) {
          data = await data;
        }

        if (!Array.isArray(data)) {
          this._items = null;
          $log('Returned object is not an array');
          return { items: [], fullCount: 0 };
        }
        this._items = data;
      } catch (err: any) {
        $log('Failed to load array data source', err);
        return { items: [], fullCount: 0 };
      }
    }

    const cached = this._items;
    if (cached === null) {
      return { items: [], fullCount: 0 };
    }

    const keys = options.filter !== undefined ? Object.keys(options.filter) : [];
    const filteredItems: T[] = cached.filter((item: any) => {
      if (keys.length > 0) {
        for (let i = 0; i < keys.length; ++i) {
          if (options.filter !== undefined) {
            if (item[keys[i]].toString().toLowerCase().startsWith(options.filter[keys[i]].filter.toString().toLowerCase())) {
              return true;
            }
          }
        }
      } else {
        return true;
      }
    });

    const resultItems = [...this.getStaticData(), ...filteredItems];

    return {
      items: resultItems,
      fullCount: resultItems.length
    };
  }

  public find(predicate: (x: T) => boolean): T | undefined {
    return this._items?.find(predicate);
  }
}
